import {
  Box,
  InternalIcon,
  Link,
  ListItemText,
  Theme,
  Typography,
  UnorderedList,
  UnorderedListItem,
  useMediaQuery,
  useTheme
} from "@carrotfertility/carotene-core"
import React from "react"
import { DigitalFrontDoorSectionLayout } from "../DigitalFrontDoorSectionLayout"
import { FormattedMessage, useIntl } from "react-intl"
import CarrotMemberWithCircleEnglish from "../resources/images/carrot-member-blue-circle-english.png"
import CarrotMemberWithCircleSpanish from "../resources/images/carrot-member-blue-circle-spanish.png"
import { FillYourPrescriptionButton } from "./FillYourPrescriptionButton"
import { GetACarrotPlanButton } from "./GetACarrotPlanButton"
import { useDigitalFrontDoorBreakpointViews } from "../hooks/useDigitalFrontDoorBreakpointViews"

function CarrotMemberImageGroup(): JSX.Element {
  const isMobileView = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))
  const intl = useIntl()
  const { locale } = intl
  return (
    <Box display="flex" flex={1.5}>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box
          minWidth={isMobileView ? "178px" : "250px"}
          alignSelf="flex-end"
          flex={3}
          component="img"
          src={locale === "es-US" ? CarrotMemberWithCircleSpanish : CarrotMemberWithCircleEnglish}
          alt=""
        />
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        flex={1}
        maxWidth={isMobileView ? "8rem" : "13rem"}
      >
        <Box>
          <Typography fontSize={isMobileView ? "3rem" : "5.75rem"}>
            <FormattedMessage defaultMessage="40%" />
          </Typography>
          <Typography fontSize={isMobileView ? "0.75rem" : "0.875rem"}>
            <FormattedMessage defaultMessage="Potential savings on fertility medications" />
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

function SaveOnFertilityMedicationsList(): JSX.Element {
  const intl = useIntl()
  return (
    <UnorderedList
      sx={(theme) => ({
        "li::marker": {
          color: theme.palette.text.primary
        }
      })}
    >
      <UnorderedListItem>
        <ListItemText
          primary={
            <FormattedMessage
              defaultMessage="<bold>Save up to 40%</bold> with exclusive discounts on fertility medication"
              values={{
                bold: (boldContent) => (
                  <Typography component="span" fontWeight="bold">
                    {boldContent}
                  </Typography>
                )
              }}
            />
          }
        />
      </UnorderedListItem>
      <UnorderedListItem>
        <ListItemText
          primary={intl.formatMessage({
            defaultMessage: "Guaranteed price matching on most fertility medication orders"
          })}
        />
      </UnorderedListItem>
      <UnorderedListItem>
        <ListItemText
          primary={intl.formatMessage({
            defaultMessage: "Competitive pricing on all prescriptions"
          })}
        />
      </UnorderedListItem>
      <UnorderedListItem>
        <ListItemText
          primary={intl.formatMessage({
            defaultMessage: "Easily switch pharmacies to Alto"
          })}
        />
      </UnorderedListItem>
    </UnorderedList>
  )
}

function PricingLinks(): JSX.Element {
  const PRICE_QUOTE_LINK = "https://www.surveymonkey.com/r/3VHLDLH"
  return (
    <Box
      marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}
      marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xxxl)}
      bgcolor={(theme) => theme.palette.background.default}
      gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      display="flex"
      flexDirection="column"
      borderRadius={(theme) => theme.tokens.borderRadius.md}
      padding={(theme) => theme.spacing(theme.tokens.spacing.md, theme.tokens.spacing.lg)}
    >
      <Link
        id="price-quote-for-fertility-medication-link"
        target="_blank"
        href={PRICE_QUOTE_LINK}
        endIcon={<InternalIcon fontSize="small" color="primary" />}
      >
        <FormattedMessage defaultMessage="Get a price quote for fertility medications" />
      </Link>
      <Link
        id="price-matching-details-link"
        target="_blank"
        href="/your-benefit-guide/pharmacy"
        endIcon={<InternalIcon fontSize="small" color="primary" />}
      >
        <FormattedMessage defaultMessage="View price matching details" />
      </Link>
    </Box>
  )
}

export function FertilityMedicationSavingSection({ isInAppLockdown }: { isInAppLockdown: boolean }): JSX.Element {
  const theme = useTheme()

  const { isUnderXLScreenView } = useDigitalFrontDoorBreakpointViews()

  return (
    <Box id="save-on-fertility-meds">
      <DigitalFrontDoorSectionLayout backgroundColor={theme.palette.background.paper}>
        <Box
          flexDirection={isUnderXLScreenView ? "column-reverse" : "row"}
          display="flex"
          gap={(theme) => theme.spacing(theme.tokens.spacing.xxxl)}
          paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xxxl)}
        >
          <CarrotMemberImageGroup />
          <Box flex={1} flexDirection="column">
            <Typography marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xxl)} variant="h2">
              <FormattedMessage defaultMessage="Save up to 40% on fertility medications" />
            </Typography>
            <SaveOnFertilityMedicationsList />
            {!isInAppLockdown && <PricingLinks />}
            <Box
              marginTop={(theme) => theme.spacing(theme.tokens.spacing.lg)}
              marginBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}
            >
              {isInAppLockdown ? <GetACarrotPlanButton /> : <FillYourPrescriptionButton />}
            </Box>
          </Box>
        </Box>
      </DigitalFrontDoorSectionLayout>
    </Box>
  )
}
